import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Package, Hammer, Puzzle, Truck, Wallet, ArrowUpDown, Home as HomeIcon, Layers } from "lucide-react";
import { useProduct, http, inr } from "../lib/api";
import { PlankSymbol } from "../components/Logo";
import { Stars } from "../components/Stars";
import { PincodeChecker } from "../components/PincodeChecker";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const G = (id) => `https://static.prod-images.emergentagent.com/jobs/b9979ec9-3304-4896-8d2c-2eeafd09334a/images/${id}.jpeg`;
const BUILD = G("0ee8dbd954966d9f82ebf08ff2344b8d25c73b184d9076d75ffef24ad16c43a7");
const OAK_HOME = G("85005e24d29e8de1348f0de8cb521622d4b04eb172dde2a8b9771e4b5dddcd5d");
const WALNUT_COUPLE = G("eee90a82945f040112fece1c1b5696a345f919e330ff31bcdb5fe95d53094050");

const Hero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden">
    <PlankSymbol bar="currentColor" className="pointer-events-none absolute -left-72 top-1/2 h-[640px] w-[640px] -translate-y-1/2 text-jute/30 dark:text-linen/[0.04]" />
    <div className="container-hw relative grid items-center gap-12 py-14 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-6">
        <p className="eyebrow rise">Do-it-yourself modular furniture · Hyderabad</p>
        <h1 className="rise d1 mt-5 text-5xl leading-[1.05] sm:text-6xl lg:text-7xl">Build it together.</h1>
        <p className="rise d2 mt-6 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
          Modular furniture you put together yourself. It flat-packs to your door, builds at home with the hex key in the box, and grows with you: add a shelf anytime.
        </p>
        <div className="rise d3 mt-9 flex flex-wrap gap-3">
          <Link to="/product/pustak-rack" data-testid="hero-shop-button" className="btn-sheesham px-7 py-3.5">Shop the Pustak Rack <ArrowRight className="h-4 w-4" /></Link>
          <a href="#how-it-builds" data-testid="hero-how-button" className="btn-outline px-7 py-3.5">See how it builds</a>
        </div>
        <dl className="rise d4 mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-border pt-6 text-sm">
          {[[Package, "Flat-packed", "builds at home"], [Puzzle, "Modular", "add a shelf anytime"], [Truck, "Free delivery", "1 to 2 days in Hyderabad"]].map(([I, a, b]) => (
            <div key={a}><I className="mb-2 h-5 w-5 text-sheesham dark:text-brass" /><dt className="font-semibold">{a}</dt><dd className="text-muted-foreground">{b}</dd></div>
          ))}
        </dl>
      </div>
      <div className="relative lg:col-span-6">
        <div className="rise d2 aspect-[4/3] overflow-hidden rounded-md lg:aspect-square">
          <img src={BUILD} alt="A young Hyderabad couple building their Pustak Rack together on the floor" className="h-full w-full object-cover" />
        </div>
        <Link to="/product/pustak-rack" data-testid="hero-featured-card" className="rise d4 absolute -bottom-6 left-4 flex items-center gap-4 rounded-md border border-border bg-background/90 p-4 pr-6 shadow-xl backdrop-blur-lg transition-transform hover:-translate-y-1 sm:left-[-2rem]">
          <img src={OAK_HOME} alt="" className="h-16 w-16 rounded object-cover object-top" />
          <div>
            <p className="font-display text-lg leading-tight">Pustak Rack</p>
            <p className="text-xs text-muted-foreground">7 shelves · Oak or Walnut</p>
            <p className="mt-1 text-sm font-semibold">{inr(4999)} <span className="text-xs font-normal text-muted-foreground">excl. tax</span></p>
          </div>
        </Link>
      </div>
    </div>
  </section>
);

const NewLaunch = () => {
  const { data } = useProduct("pustak-rack");
  const [k, setK] = useState("oak");
  if (!data) return null;
  const p = data.product;
  const v = p.variants.find((x) => x.key === k) || p.variants[0];
  return (
    <section data-testid="home-new-launch" className="grid overflow-hidden bg-card lg:grid-cols-2">
      <div className="container-hw flex flex-col justify-center py-16 lg:max-w-none lg:py-24 lg:pl-[max(3rem,calc((100vw-80rem)/2+3rem))]">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brass">New in · Modular storage</p>
        <span className="mt-5 h-0.5 w-10 bg-brass" />
        <h2 className="mt-6 text-5xl sm:text-6xl">{p.name}</h2>
        <p className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground">{p.description}</p>
        <p className="mt-8 text-sm font-medium">Finish: {v.name}</p>
        <div className="mt-3 flex gap-5">
          {p.variants.map((x) => (
            <button key={x.key} data-testid={`launch-variant-${x.key}`} onClick={() => setK(x.key)} className="flex items-center gap-2 text-sm font-medium">
              <span style={{ backgroundColor: x.swatch }} className={`h-7 w-7 rounded-full ${x.key === v.key ? "ring-2 ring-foreground ring-offset-2 ring-offset-card" : ""}`} />{x.name}
            </button>
          ))}
        </div>
        <div className="mt-10 flex items-center gap-6">
          <div><p data-testid="launch-price" className="price text-4xl">{inr(p.price)}</p><p className="small-print">Excluding tax</p></div>
          <Link to={`/product/${p.slug}?finish=${v.key}`} data-testid="launch-shop-now-button" className="btn-sheesham rounded-md px-7 py-3.5">Shop now</Link>
        </div>
        <ul className="mt-10 grid max-w-lg grid-cols-3 gap-4 border-t border-border pt-5 text-sm text-muted-foreground">
          {p.features.map((f) => <li key={f}>{f}</li>)}
        </ul>
      </div>
      <div className="relative min-h-[560px] bg-jute/40">
        <img key={v.key} src={v.images[0]} alt={`${p.name} in ${v.name} finish`} className="rise absolute inset-0 h-full w-full object-cover object-top" />
      </div>
    </section>
  );
};

const WHY = [
  [Wallet, "An honest price", "You do the last bit of work at home, so you don't pay for a showroom, a middleman or a carpenter's visit."],
  [ArrowUpDown, "Fits any lift", "Everything ships flat. No hauling a built cabinet up the stairs or squeezing it round a corner."],
  [HomeIcon, "Moves with you", "Bolts, not glue. Take it apart for the next flat and build it again in the new one."],
  [Layers, "Grows with you", "Start with one rack. Add a shelf anytime. Add-ons fit the same system."],
];

const WhyDIY = () => (
  <section data-testid="home-why-diy" className="container-hw py-20 lg:py-28">
    <div className="max-w-2xl">
      <p className="eyebrow">Why do it yourself</p>
      <h2 className="mt-3 text-3xl sm:text-4xl">Furniture that's yours, because you built it.</h2>
      <p className="mt-5 text-muted-foreground">Do-it-yourself modular furniture isn't a compromise. It's how a well-made piece gets cheaper to buy, easier to move and simple to add to.</p>
    </div>
    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {WHY.map(([I, t, d]) => (
        <div key={t} className="rounded-md border border-border bg-card p-6 transition-transform duration-300 hover:-translate-y-1">
          <I className="h-6 w-6 text-sheesham dark:text-brass" />
          <p className="mt-5 font-display text-xl">{t}</p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Modular = () => (
  <section data-testid="home-modular" className="border-y border-border bg-muted/50">
    <div className="container-hw grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
      <div className="lg:col-span-5">
        <p className="eyebrow">One modular system</p>
        <h2 className="mt-3 text-3xl sm:text-4xl">Start with one rack. Add as life fills up.</h2>
        <ol className="mt-10 space-y-6">
          {[["Start", "Build one Pustak Rack: seven open shelves for books, plants and everything in between."], ["Add", "Need more room? Add a shelf anytime. It fits the frame you already have."], ["Extend", "Add-ons fit the same system, so new pieces work with the ones already in your home."]].map(([t, d], i) => (
            <li key={t} className="flex gap-5">
              <span className="font-display text-3xl text-brass">0{i + 1}</span>
              <div><p className="font-semibold">{t}</p><p className="mt-1 text-sm leading-relaxed text-muted-foreground">{d}</p></div>
            </li>
          ))}
        </ol>
        <Link to="/product/pustak-rack" data-testid="modular-shop-button" className="btn-sheesham mt-10">Start with the Pustak Rack</Link>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:col-span-7">
        <img src={OAK_HOME} alt="Pustak Rack in Oak in a Hyderabad study" className="aspect-[2/3] w-full rounded-md object-cover" />
        <img src={WALNUT_COUPLE} alt="A couple styling their Walnut Pustak Rack in a Hyderabad living room" className="mt-12 aspect-[2/3] w-full rounded-md object-cover" />
      </div>
    </div>
  </section>
);

const STEPS = [
  [Package, "Unbox", "It flat-packs to your door. Every part is numbered, and the illustrated guide shows each step."],
  [Hammer, "Build", "The hex key is in the box. Two pairs of hands, one evening, no carpenter."],
  [Puzzle, "Grow", "Add a shelf anytime. Add-ons fit the same system."],
];

const HowItBuilds = () => (
  <section id="how-it-builds" data-testid="how-it-builds" className="grain relative bg-walnut text-linen">
    <div className="container-hw relative grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
      <div className="lg:col-span-5">
        <div className="aspect-[4/5] overflow-hidden rounded-md">
          <img src={OAK_HOME} alt="A finished Pustak Rack in Oak, filled with books and plants" className="h-full w-full object-cover" />
        </div>
      </div>
      <div className="flex flex-col justify-center lg:col-span-6 lg:col-start-7">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-brass">How it builds</p>
        <h2 className="mt-4 text-4xl sm:text-5xl">One evening. No carpenter.</h2>
        <p className="mt-5 max-w-lg text-linen/75">Do it yourself, not by yourself. We design every part so two people who have never held a hex key can build it in one evening, and we answer on care@hwfurniture.in if you get stuck.</p>
        <ol className="mt-10 space-y-8">
          {STEPS.map(([I, t, d], i) => (
            <li key={t} className="flex gap-5">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-brass/50 text-brass"><I className="h-5 w-5" /></span>
              <div><p className="font-display text-2xl">{i + 1}. {t}</p><p className="mt-1 text-linen/75">{d}</p></div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

const AREAS = ["Gachibowli", "Kondapur", "Madhapur", "HITEC City", "Financial District", "Kokapet", "Manikonda", "Kukatpally", "Miyapur", "Banjara Hills", "Jubilee Hills", "Begumpet", "Secunderabad", "Tolichowki", "LB Nagar", "Uppal", "Kompally", "Shamshabad"];

const Localities = () => (
  <section data-testid="home-localities" className="container-hw grid gap-10 py-16 lg:grid-cols-12 lg:py-20">
    <div className="lg:col-span-5">
      <p className="eyebrow">Hyderabad only, for now</p>
      <h2 className="mt-3 text-3xl sm:text-4xl">Made for Hyderabad. Delivered across it in 1 to 2 days.</h2>
      <p className="mt-5 text-muted-foreground">We are a Hyderabad business and deliver only in Hyderabad and Secunderabad for now. Our own team carries the box to your door, lift or no lift. Free, always.</p>
    </div>
    <div className="lg:col-span-7">
      <div className="flex flex-wrap gap-2">
        {AREAS.map((a) => <span key={a} className="chip px-3.5 py-1.5 text-sm">{a}</span>)}
      </div>
      <div className="mt-6 max-w-md"><PincodeChecker /></div>
    </div>
  </section>
);

const Testimonials = () => {
  const { data = [] } = useQuery({ queryKey: ["recent-reviews"], queryFn: () => http.get("/reviews/recent").then((r) => r.data) });
  if (data.length === 0) return null;
  return (
    <section data-testid="home-testimonials" className="border-y border-border bg-muted/50 py-20">
      <div className="container-hw">
        <p className="eyebrow">Built in Hyderabad homes</p>
        <h2 className="mt-3 text-3xl sm:text-4xl">Notes from the floor.</h2>
        <div className="no-scrollbar mt-10 flex snap-x gap-5 overflow-x-auto pb-2">
          {data.map((r) => (
            <figure key={r.id} className="w-[300px] shrink-0 snap-start rounded-md border border-border bg-card p-6 sm:w-[360px]">
              <Stars value={r.rating} />
              <blockquote className="mt-4 font-display text-lg leading-snug">"{r.comment}"</blockquote>
              <figcaption className="mt-4 text-sm text-muted-foreground">{r.name}, {r.city}, Hyderabad</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQS = [
  ["Do I need a carpenter or special tools?", "No. The Pustak Rack comes with the hex key, numbered parts and an illustrated guide. If you can follow a recipe, you can build it."],
  ["How long does it take to build?", "About 40 minutes for two people. One of you holds, the other turns the key."],
  ["Can I add shelves later?", "Yes. That's the point of modular. You can add a shelf anytime, and add-ons fit the same system."],
  ["Is it safe with kids around?", "Every rack includes a wall anchor kit. Fix it to the wall, especially if you have small children or pets."],
  ["How much does delivery cost?", "Nothing. Anywhere in Hyderabad and Secunderabad, from Kompally to Shamshabad, the box arrives in 1 to 2 days. We deliver only within Hyderabad and Secunderabad for now."],
  ["Is GST included in the price?", "The Pustak Rack is priced at ₹4,999 excluding tax. GST is added at checkout so you see the full amount before you place the order."],
  ["How do I pay?", "Right now you pay by cash or UPI when the box arrives at your door. Online payment is coming soon."],
  ["What if a part is damaged or missing?", "Email care@hwfurniture.in with a photo. In Hyderabad we send the replacement part free within 2 working days."],
];

const FAQ = () => (
  <section id="faq" data-testid="home-faq" className="container-hw grid gap-10 py-20 lg:grid-cols-12 lg:py-28">
    <div className="lg:col-span-4">
      <p className="eyebrow">Questions</p>
      <h2 className="mt-3 text-3xl sm:text-4xl">Before you order.</h2>
    </div>
    <Accordion type="single" collapsible className="lg:col-span-8">
      {FAQS.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`}>
          <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-5 text-left text-base font-semibold hover:no-underline">{q}</AccordionTrigger>
          <AccordionContent className="pb-5 text-base leading-relaxed text-muted-foreground">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <NewLaunch />
      <WhyDIY />
      <HowItBuilds />
      <Modular />
      <Localities />
      <Testimonials />
      <FAQ />
    </>
  );
}
